/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import { memo } from 'react';

import { css, jsx } from '@compiled/react';

import { B100 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { zIndex } from '../../../common/constants';
import {
	useExtendedContentLayout,
	useExtendedContentWidth,
	useHasExtendedContent,
} from '../../../common/context/extended-content';
import { useListWidth } from '../../../common/context/list';
import { getRowClassName } from '../../../common/context/side-effect-marshal/style-marshal/utils';
import { ExtendedContentItems } from '../../../common/types/extended-content';
import { RenderExtendedContent } from '../../../renderers';

import { ExtendedContentDivider } from './divider';

const extendedContentContainerStyles = css({
	position: 'sticky',
	display: 'flex',
	overflowX: 'hidden',
	alignItems: 'center',
	boxSizing: 'border-box',
	transition: 'background-color 100ms linear',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	zIndex: zIndex.LIST,
	'&:focus': {
		outline: 'none',
	},
	'&:focus-visible': {
		boxShadow: `inset 0 0 0 2px ${token('color.border.focused', B100)}`,
	},
});

const cellStyles = css({
	position: 'absolute',
});

const dividerWidth = token('space.025');

/**
 * Props for the ExtendedContent component.
 */
export type ExtendedContentComponentProps = {
	/** Unique identifier for the row */
	id: string;
	/** Optional parent identifier for nested rows */
	parentId?: string;
	/** Height of the row in pixels */
	itemHeight: number;
	/** Background color of the row */
	backgroundColor: string;
	/** Function to render the content of each extended cell */
	renderExtendedContent: RenderExtendedContent;
	/** Configuration for the extended content columns */
	extendedContent: ExtendedContentItems;
};

/**
 * ExtendedContent is a component that renders additional elements between the List and Chart sections
 * of the Timeline Table. It provides a way to display structured data in a grid-like format while
 * maintaining the Timeline Table's row hierarchy and styling.
 *
 * The component:
 * - Maintains consistent styling with ListItem and ChartItem
 * - Uses sticky positioning to stay aligned with the List
 * - Supports the same hover and focus states as other row components
 * - Renders content using the provided renderer function
 * - Maintains proper grid semantics with ARIA roles
 *
 * @example
 * ```tsx
 * <ExtendedContent
 *   id="row-1"
 *   itemHeight={40}
 *   backgroundColor="white"
 *   extendedContent={[
 *     { key: 'status', width: 120 },
 *     { key: 'assignee', width: 180 },
 *   ]}
 *   renderExtendedContent={({ id, key }) => (
 *     <div>{`${key} content`}</div>
 *   )}
 * />
 * ```
 */
export const ExtendedContent = ({
	id,
	parentId,
	itemHeight,
	backgroundColor,
	renderExtendedContent,
	extendedContent,
}: ExtendedContentComponentProps) => {
	const [listWidth] = useListWidth();
	const [extendedContentWidth] = useExtendedContentWidth();
	const [hasExtendedContent] = useHasExtendedContent();
	const [columnLayout] = useExtendedContentLayout();

	if (!hasExtendedContent) {
		return null;
	}

	return (
		<div
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className={getRowClassName(id, parentId).className}
			css={extendedContentContainerStyles}
			style={{
				left: `${listWidth}px`,
				width: `${extendedContentWidth}px`,
				height: `${itemHeight}px`,
				backgroundColor,
			}}
		>
			{/* Content cells */}
			{extendedContent.map(({ key }, index) => (
				<div
					key={key}
					role="gridcell"
					css={cellStyles}
					style={{
						left: columnLayout[index].left,
						width: columnLayout[index].width,
					}}
				>
					{renderExtendedContent({ id, key })}
				</div>
			))}

			{/* Dividers between columns */}
			{extendedContent.map((_, index) => {
				const isLastColumn = index === extendedContent.length - 1;

				const columnRight = columnLayout[index].left + columnLayout[index].width;

				const left = isLastColumn
					? `${extendedContentWidth - 2}px`
					: `calc(${columnRight}px - ${dividerWidth})`;

				// Divider is positioned at the right edge of each column
				return (
					<ExtendedContentDivider key={`divider-${index}`} left={left} height={`${itemHeight}px`} />
				);
			})}
		</div>
	);
};

export default memo<ExtendedContentComponentProps>(ExtendedContent);
